import type { DesignThemeCssTokens } from './design-theme-css-tokens.types';
import {
  getGetdesignDeveloperToolThemeCss,
  withSemanticThemeColors,
} from './getdesign-developer-tool-themes';

export const DESIGN_THEME_STORAGE_KEY = 'attune:design-theme:v1';
export const DESIGN_THEME_TOKENS_STORAGE_KEY = 'attune:design-theme-tokens:v14';
export const DEFAULT_DESIGN_THEME_ID = 'superhuman';
export const DEFAULT_DESIGN_THEME_MODE = 'light';

/** Optional presets from the getdesign.md collection (`npx getdesign@latest add <slug>`). */
export type DesignThemeId =
  | 'liquid-glass'
  | 'cursor'
  | 'vercel'
  | 'warp'
  | 'raycast'
  | 'expo'
  | 'lovable'
  | 'superhuman';
export type DesignThemeMode = 'system' | 'light' | 'dark';
export type ResolvedDesignThemeMode = Exclude<DesignThemeMode, 'system'>;

export interface DesignThemePreference {
  themeId: DesignThemeId;
  mode: DesignThemeMode;
}

export interface DesignThemeOption {
  id: DesignThemeId;
  label: string;
  description: string;
  swatches: readonly string[];
}

type DesignThemeCssTokenMap = Record<
  DesignThemeId,
  Record<ResolvedDesignThemeMode, DesignThemeCssTokens>
>;

const CSS_TOKEN_VARIABLES = [
  ['bg', '--attune-app-bg'],
  ['bgWash', '--attune-app-bg-wash'],
  ['bgDepth', '--attune-app-bg-depth'],
  ['meshDot', '--attune-app-mesh-dot'],
  ['ambientGlow', '--attune-app-ambient-glow'],
  ['glassBg', '--attune-glass-bg'],
  ['glassBgSubtle', '--attune-glass-bg-subtle'],
  ['glassBgMuted', '--attune-glass-bg-muted'],
  ['glassBgBase', '--attune-glass-bg-base'],
  ['glassBgElevated', '--attune-glass-bg-elevated'],
  ['glassBgCard', '--attune-glass-bg-card'],
  ['glassFill', '--attune-glass-fill'],
  ['glassInteractiveHover', '--attune-glass-interactive-hover'],
  ['glassTraceHeaderHover', '--attune-glass-trace-header-hover'],
  ['glassBorder', '--attune-glass-border'],
  ['glassBorderSubtle', '--attune-glass-border-subtle'],
  ['uiSurfacePanel', '--attune-ui-surface-panel'],
  ['uiSurfaceCard', '--attune-ui-surface-card'],
  ['uiSurfaceRail', '--attune-ui-surface-rail'],
  ['uiSurfacePopover', '--attune-ui-surface-popover'],
  ['uiSurfaceDrawer', '--attune-ui-surface-drawer'],
  ['uiSurfaceDrawerHeader', '--attune-ui-surface-drawer-header'],
  ['uiSurfaceDrawerMuted', '--attune-ui-surface-drawer-muted'],
  ['uiSurfaceInput', '--attune-ui-surface-input'],
  ['uiSurfaceRowHover', '--attune-ui-surface-row-hover'],
  ['uiSurfaceSelected', '--attune-ui-surface-selected'],
  ['uiSurfaceWarning', '--attune-ui-surface-warning'],
  ['uiBorderDefault', '--attune-ui-border-default'],
  ['uiBorderSubtle', '--attune-ui-border-subtle'],
  ['uiBorderStrong', '--attune-ui-border-strong'],
  ['uiBorderAccent', '--attune-ui-border-accent'],
  ['uiTextPrimary', '--attune-ui-text-primary'],
  ['uiTextSecondary', '--attune-ui-text-secondary'],
  ['uiTextTertiary', '--attune-ui-text-tertiary'],
  ['uiTextAccent', '--attune-ui-text-accent'],
  ['accentSolid', '--attune-accent-solid'],
  ['accentOnSolid', '--attune-accent-on-solid'],
  ['accentFg', '--attune-accent-fg'],
  ['accentMuted', '--attune-accent-muted'],
  ['accentSubtle', '--attune-accent-subtle'],
  ['accentEmphasized', '--attune-accent-emphasized'],
  ['accentFocusRing', '--attune-accent-focus-ring'],
  ['accentChip', '--attune-accent-chip'],
  ['accentTraceSurface', '--attune-accent-trace-surface'],
  ['accentTraceSurfaceHover', '--attune-accent-trace-surface-hover'],
  ['accentTraceBorder', '--attune-accent-trace-border'],
  ['accentTraceBorderHover', '--attune-accent-trace-border-hover'],
  ['accentAnswerHoverBorder', '--attune-accent-answer-hover-border'],
  ['accentSecondarySubtle', '--attune-accent-secondary-subtle'],
  ['accentCommandBorder', '--attune-accent-command-border'],
  ['accentStart', '--attune-accent-start'],
  ['accentEnd', '--attune-accent-end'],
  ['accentShadow', '--attune-accent-shadow'],
  ['accentShadowHover', '--attune-accent-shadow-hover'],
  ['selectionBg', '--attune-selection-bg'],
  ['scrollbarThumb', '--attune-scrollbar-thumb'],
  ['scrollbarThumbHover', '--attune-scrollbar-thumb-hover'],
  ['shadowElevated', '--attune-shadow-elevated'],
  ['shadowFloating', '--attune-shadow-floating'],
  ['shadowPressed', '--attune-shadow-pressed'],
  ['shadowInner', '--attune-shadow-inner'],
  ['shadowGlow', '--attune-shadow-glow'],
  ['shadowSubtle', '--attune-shadow-subtle'],
  ['shadowRing', '--attune-shadow-ring'],
  ['luminousBlue', '--attune-luminous-blue'],
  ['luminousBlueGlow', '--attune-luminous-blue-glow'],
  ['luminousPurple', '--attune-luminous-purple'],
  ['luminousPurpleGlow', '--attune-luminous-purple-glow'],
  ['luminousPink', '--attune-luminous-pink'],
  ['luminousPinkGlow', '--attune-luminous-pink-glow'],
  ['luminousOrange', '--attune-luminous-orange'],
  ['luminousOrangeGlow', '--attune-luminous-orange-glow'],
  ['luminousGreen', '--attune-luminous-green'],
  ['luminousGreenGlow', '--attune-luminous-green-glow'],
  ['luminousTeal', '--attune-luminous-teal'],
  ['luminousTealGlow', '--attune-luminous-teal-glow'],
  ['luminousCyan', '--attune-luminous-cyan'],
  ['luminousCyanGlow', '--attune-luminous-cyan-glow'],
  ['luminousIndigo', '--attune-luminous-indigo'],
  ['luminousIndigoGlow', '--attune-luminous-indigo-glow'],
  ['statusSuccessFg', '--attune-status-success-fg'],
  ['statusSuccessBg', '--attune-status-success-bg'],
  ['statusSuccessBorder', '--attune-status-success-border'],
  ['statusWarningFg', '--attune-status-warning-fg'],
  ['statusWarningBg', '--attune-status-warning-bg'],
  ['statusWarningBorder', '--attune-status-warning-border'],
  ['statusDangerFg', '--attune-status-danger-fg'],
  ['statusDangerBg', '--attune-status-danger-bg'],
  ['statusDangerBorder', '--attune-status-danger-border'],
  ['statusInfoFg', '--attune-status-info-fg'],
  ['statusInfoBg', '--attune-status-info-bg'],
  ['statusInfoBorder', '--attune-status-info-border'],
  ['statusNeutralFg', '--attune-status-neutral-fg'],
  ['statusNeutralBg', '--attune-status-neutral-bg'],
  ['statusNeutralBorder', '--attune-status-neutral-border'],
  ['rankGoldFg', '--attune-rank-gold-fg'],
  ['rankGoldBg', '--attune-rank-gold-bg'],
  ['rankGoldBorder', '--attune-rank-gold-border'],
  ['rankSilverFg', '--attune-rank-silver-fg'],
  ['rankSilverBg', '--attune-rank-silver-bg'],
  ['rankSilverBorder', '--attune-rank-silver-border'],
  ['rankBronzeFg', '--attune-rank-bronze-fg'],
  ['rankBronzeBg', '--attune-rank-bronze-bg'],
  ['rankBronzeBorder', '--attune-rank-bronze-border'],
  ['dataVisibility', '--attune-data-visibility'],
  ['dataRank3', '--attune-data-rank3'],
  ['dataRank1', '--attune-data-rank1'],
  ['dataMissed', '--attune-data-missed'],
  ['dataPositive', '--attune-data-positive'],
  ['dataNegative', '--attune-data-negative'],
  ['dataNeutral', '--attune-data-neutral'],
  ['dataOrange', '--attune-data-orange'],
  ['dataPurple', '--attune-data-purple'],
  ['dataCyan', '--attune-data-cyan'],
  ['dataPink', '--attune-data-pink'],
  ['dataTooltipBg', '--attune-data-tooltip-bg'],
  ['dataGrid', '--attune-data-grid'],
  ['dataAxis', '--attune-data-axis'],
  ['dataCursor', '--attune-data-cursor'],
  ['chartSeries1', '--attune-chart-series-1'],
  ['chartSeries2', '--attune-chart-series-2'],
  ['chartSeries3', '--attune-chart-series-3'],
  ['chartSeries4', '--attune-chart-series-4'],
  ['chartSeries5', '--attune-chart-series-5'],
  ['chartSeries6', '--attune-chart-series-6'],
  ['chartSeries7', '--attune-chart-series-7'],
  ['chartSeries8', '--attune-chart-series-8'],
  ['chartSeries9', '--attune-chart-series-9'],
  ['chartSeries10', '--attune-chart-series-10'],
  ['chartSeries11', '--attune-chart-series-11'],
  ['chartSeries12', '--attune-chart-series-12'],
  ['chartSeries13', '--attune-chart-series-13'],
  ['chartSeries14', '--attune-chart-series-14'],
  ['chartSeries15', '--attune-chart-series-15'],
] as const satisfies readonly (readonly [keyof DesignThemeCssTokens, string])[];

export const DESIGN_THEME_OPTIONS = [
  {
    id: 'superhuman',
    label: 'Nocturne',
    description: 'Orchid surfaces, plum ink, and vivid violet actions.',
    swatches: ['#EAE7EF', '#FAF9FC', '#30283F', '#7539B5'],
  },
  {
    id: 'liquid-glass',
    label: 'Black & White',
    description: 'Clean white and deep black, with soft neutral surfaces.',
    swatches: ['#EBEBEB', '#FFFFFF', '#171717', '#000000'],
  },
  {
    id: 'raycast',
    label: 'Command',
    description: 'Bright white, navy ink, and electric cobalt.',
    swatches: ['#EAEDF3', '#FCFCFE', '#19243B', '#214CE5'],
  },
  {
    id: 'warp',
    label: 'Warp',
    description: 'Golden parchment, warm charcoal, and amber actions.',
    swatches: ['#EAE4D6', '#FAF8F2', '#343025', '#795200'],
  },
  {
    id: 'cursor',
    label: 'Graphite',
    description: 'Warm graphite surfaces with a precise vermilion action color.',
    swatches: ['#ECECE7', '#FAFAF7', '#26251E', '#D04200'],
  },
  {
    id: 'vercel',
    label: 'Ledger',
    description: 'Sage surfaces, forest ink, and evergreen actions.',
    swatches: ['#E6ECE8', '#F9FBFA', '#1D3429', '#006747'],
  },
  {
    id: 'expo',
    label: 'Harbor',
    description: 'Sea-glass surfaces with deep petrol and clear teal.',
    swatches: ['#E5ECEE', '#F9FBFC', '#173740', '#00677D'],
  },
  {
    id: 'lovable',
    label: 'Copper',
    description: 'Clay surfaces, espresso ink, and red copper accents.',
    swatches: ['#EFE5E0', '#FCF9F7', '#402D27', '#AD3F43'],
  },
] as const satisfies readonly DesignThemeOption[];

export const DESIGN_THEME_MODE_OPTIONS = [
  { value: 'system', label: 'System' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
] as const satisfies readonly { value: DesignThemeMode; label: string }[];

const DESIGN_THEME_IDS = DESIGN_THEME_OPTIONS.map(option => option.id);
const DESIGN_THEME_MODES = DESIGN_THEME_MODE_OPTIONS.map(option => option.value);

function linearizeSrgbChannel(c: number): number {
  const x = c / 255;
  return x <= 0.04045 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4;
}

/** Relative luminance 0–1 (sRGB) — for choosing text on `accentSolid` fills. */
function srgbLuminance(r: number, g: number, b: number): number {
  return (
    0.2126 * linearizeSrgbChannel(r) +
    0.7152 * linearizeSrgbChannel(g) +
    0.0722 * linearizeSrgbChannel(b)
  );
}

/** Best-effort readable foreground for a hex `accentSolid` when `accentOnSolid` is absent. */
function contrastFgForSolid(solid: string): string {
  const s = solid.trim();
  const longHex = /^#([0-9a-f]{6})$/i.exec(s);
  let r: number;
  let g: number;
  let b: number;
  if (longHex) {
    r = parseInt(longHex[1].slice(0, 2), 16);
    g = parseInt(longHex[1].slice(2, 4), 16);
    b = parseInt(longHex[1].slice(4, 6), 16);
  } else {
    const shortHex = /^#([0-9a-f]{3})$/i.exec(s);
    if (!shortHex) return '#FFFFFF';
    const h = shortHex[1];
    r = parseInt(h[0] + h[0], 16);
    g = parseInt(h[1] + h[1], 16);
    b = parseInt(h[2] + h[2], 16);
  }
  return srgbLuminance(r, g, b) > 0.45 ? '#171717' : '#FFFFFF';
}

function ensureReadableThemeTokens(tokens: DesignThemeCssTokens): DesignThemeCssTokens {
  const on = tokens.accentOnSolid?.trim();
  const focusRing = tokens.accentFocusRing?.trim();

  return {
    ...tokens,
    accentOnSolid: on || contrastFgForSolid(tokens.accentSolid),
    accentFocusRing:
      focusRing && focusRing.toLowerCase() !== 'transparent'
        ? focusRing
        : tokens.accentFg || tokens.uiTextAccent,
  };
}

function normalizeDesignThemeCssTokenMap(map: DesignThemeCssTokenMap): DesignThemeCssTokenMap {
  const out = {} as DesignThemeCssTokenMap;
  for (const id of DESIGN_THEME_IDS) {
    const entry = map[id];
    if (!entry) continue;
    out[id] = {
      light: ensureReadableThemeTokens(withSemanticThemeColors(entry.light, 'light')),
      dark: ensureReadableThemeTokens(withSemanticThemeColors(entry.dark, 'dark')),
    };
  }
  return out;
}

export const DEFAULT_DESIGN_THEME_PREFERENCE: DesignThemePreference = {
  themeId: DEFAULT_DESIGN_THEME_ID,
  mode: DEFAULT_DESIGN_THEME_MODE,
};

const liquidGlassLight: DesignThemeCssTokens = {
  bg: '#F4F6F8',
  bgWash: '#FAFBFC',
  bgDepth: '#E9EDF1',
  meshDot: 'rgba(23, 23, 23, 0.14)',
  ambientGlow: 'rgba(255, 255, 255, 0)',
  glassBg: 'rgba(255, 255, 255, 0.92)',
  glassBgSubtle: 'rgba(255, 255, 255, 0.86)',
  glassBgMuted: 'rgba(0, 0, 0, 0.025)',
  glassBgBase: '#F4F6F8',
  glassBgElevated: '#FFFFFF',
  glassBgCard: 'rgba(255, 255, 255, 0.84)',
  glassFill: 'rgba(0, 0, 0, 0.025)',
  glassInteractiveHover: 'rgba(0, 0, 0, 0.035)',
  glassTraceHeaderHover: 'rgba(0, 0, 0, 0.035)',
  glassBorder: '#E3E3E4',
  glassBorderSubtle: 'rgba(0, 0, 0, 0.06)',
  uiSurfacePanel: 'rgba(255, 255, 255, 0.72)',
  uiSurfaceCard: '#FFFFFF',
  uiSurfaceRail: '#F4F6F8',
  uiSurfacePopover: '#FFFFFF',
  uiSurfaceDrawer: '#F7F9FB',
  uiSurfaceDrawerHeader: '#F0F3F6',
  uiSurfaceDrawerMuted: 'rgba(0, 0, 0, 0.025)',
  uiSurfaceInput: 'rgba(0, 0, 0, 0.025)',
  uiSurfaceRowHover: 'rgba(0, 0, 0, 0.035)',
  uiSurfaceSelected: 'rgba(0, 0, 0, 0.04)',
  uiSurfaceWarning: 'rgba(255, 149, 0, 0.1)',
  uiBorderDefault: '#D6DBE1',
  uiBorderSubtle: 'rgba(0, 0, 0, 0.06)',
  uiBorderStrong: 'rgba(0, 0, 0, 0.16)',
  uiBorderAccent: 'rgba(23, 23, 23, 0.28)',
  uiTextPrimary: '#24282E',
  uiTextSecondary: '#555E69',
  uiTextTertiary: '#606A76',
  uiTextAccent: '#171717',
  accentSolid: '#171717',
  accentOnSolid: '#FFFFFF',
  accentFg: '#171717',
  accentMuted: 'rgba(23, 23, 23, 0.24)',
  accentSubtle: 'rgba(23, 23, 23, 0.08)',
  accentEmphasized: '#363B43',
  accentFocusRing: 'transparent',
  accentChip: 'rgba(23, 23, 23, 0.07)',
  accentTraceSurface: 'rgba(23, 23, 23, 0.035)',
  accentTraceSurfaceHover: 'rgba(23, 23, 23, 0.06)',
  accentTraceBorder: 'rgba(23, 23, 23, 0.12)',
  accentTraceBorderHover: 'rgba(23, 23, 23, 0.16)',
  accentAnswerHoverBorder: 'rgba(23, 23, 23, 0.2)',
  accentSecondarySubtle: 'rgba(90, 90, 90, 0.1)',
  accentCommandBorder: 'rgba(23, 23, 23, 0.35)',
  accentStart: '#171717',
  accentEnd: '#3A3A3A',
  accentShadow: 'rgba(23, 23, 23, 0.18)',
  accentShadowHover: 'rgba(23, 23, 23, 0.26)',
  selectionBg: 'rgba(23, 23, 23, 0.12)',
  scrollbarThumb: 'rgba(0, 0, 0, 0.15)',
  scrollbarThumbHover: 'rgba(0, 0, 0, 0.25)',
  shadowElevated: '0 2px 10px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.03)',
  shadowFloating: '0 8px 18px rgba(54, 45, 34, 0.055), 0 1px 2px rgba(54, 45, 34, 0.045)',
  shadowPressed: '0 1px 2px rgba(0, 0, 0, 0.05), inset 0 1px 1px rgba(0, 0, 0, 0.025)',
  shadowInner: 'inset 0 1px 1px rgba(255, 255, 255, 0.4), inset 0 -1px 1px rgba(0, 0, 0, 0.02)',
  shadowGlow: '0 0 8px rgba(23, 23, 23, 0.12), 0 2px 6px rgba(0, 0, 0, 0.05)',
  shadowSubtle: '0 1px 4px rgba(0, 0, 0, 0.035)',
  shadowRing: 'none',
  luminousBlue: '#171717',
  luminousBlueGlow: 'rgba(23, 23, 23, 0.24)',
  luminousPurple: '#AF52DE',
  luminousPurpleGlow: 'rgba(175, 82, 222, 0.35)',
  luminousPink: '#FF2D55',
  luminousPinkGlow: 'rgba(255, 45, 85, 0.35)',
  luminousOrange: '#FF9500',
  luminousOrangeGlow: 'rgba(255, 149, 0, 0.35)',
  luminousGreen: '#34C759',
  luminousGreenGlow: 'rgba(52, 199, 89, 0.35)',
  luminousTeal: '#5AC8FA',
  luminousTealGlow: 'rgba(90, 200, 250, 0.35)',
  luminousCyan: '#32D4DE',
  luminousCyanGlow: 'rgba(50, 212, 222, 0.35)',
  luminousIndigo: '#5856D6',
  luminousIndigoGlow: 'rgba(88, 86, 214, 0.35)',
  statusSuccessFg: '#1F8F3A',
  statusSuccessBg: 'rgba(52, 199, 89, 0.11)',
  statusSuccessBorder: 'rgba(52, 199, 89, 0.26)',
  statusWarningFg: '#B66200',
  statusWarningBg: 'rgba(255, 149, 0, 0.13)',
  statusWarningBorder: 'rgba(255, 149, 0, 0.3)',
  statusDangerFg: '#D83B3B',
  statusDangerBg: 'rgba(255, 45, 85, 0.11)',
  statusDangerBorder: 'rgba(255, 45, 85, 0.28)',
  statusInfoFg: '#171717',
  statusInfoBg: 'rgba(23, 23, 23, 0.07)',
  statusInfoBorder: 'rgba(23, 23, 23, 0.16)',
  statusNeutralFg: 'rgba(0, 0, 0, 0.64)',
  statusNeutralBg: 'rgba(0, 0, 0, 0.045)',
  statusNeutralBorder: 'rgba(0, 0, 0, 0.08)',
  rankGoldFg: '#B7791F',
  rankGoldBg: 'rgba(251, 191, 36, 0.18)',
  rankGoldBorder: 'rgba(251, 191, 36, 0.44)',
  rankSilverFg: '#64748B',
  rankSilverBg: 'rgba(148, 163, 184, 0.15)',
  rankSilverBorder: 'rgba(148, 163, 184, 0.36)',
  rankBronzeFg: '#B46A2D',
  rankBronzeBg: 'rgba(180, 120, 60, 0.15)',
  rankBronzeBorder: 'rgba(180, 120, 60, 0.34)',
  dataVisibility: '#171717',
  dataRank3: '#D69E2E',
  dataRank1: '#38A169',
  dataMissed: '#EF4444',
  dataPositive: '#34C759',
  dataNegative: '#FF3B30',
  dataNeutral: '#A0AEC0',
  dataOrange: '#FF7900',
  dataPurple: '#8B5CF6',
  dataCyan: '#06B6D4',
  dataPink: '#EC4899',
  dataTooltipBg: 'rgba(255, 255, 255, 0.97)',
  dataGrid: 'rgba(0, 0, 0, 0.05)',
  dataAxis: '#6B7280',
  dataCursor: 'rgba(0, 0, 0, 0.08)',
  chartSeries1: '#171717',
  chartSeries2: '#AF52DE',
  chartSeries3: '#FF9500',
  chartSeries4: '#34C759',
  chartSeries5: '#FF2D55',
  chartSeries6: '#32D4DE',
  chartSeries7: '#5856D6',
  chartSeries8: '#FF7900',
  chartSeries9: '#06B6D4',
  chartSeries10: '#8B5CF6',
  chartSeries11: '#D69E2E',
  chartSeries12: '#38A169',
  chartSeries13: '#EC4899',
  chartSeries14: '#64748B',
  chartSeries15: '#B46A2D',
};

const liquidGlassDark: DesignThemeCssTokens = {
  ...liquidGlassLight,
  bg: '#050505',
  bgWash: '#08090C',
  bgDepth: '#020203',
  meshDot: 'rgba(237, 237, 237, 0.16)',
  ambientGlow: 'rgba(0, 0, 0, 0)',
  glassBg: 'rgba(5, 5, 5, 0.88)',
  glassBgSubtle: 'rgba(9, 10, 13, 0.78)',
  glassBgMuted: 'rgba(255, 255, 255, 0.06)',
  glassBgBase: '#090A0D',
  glassBgElevated: '#12141A',
  glassBgCard: '#0E1015',
  glassFill: 'rgba(255, 255, 255, 0.06)',
  glassInteractiveHover: 'rgba(255, 255, 255, 0.06)',
  glassTraceHeaderHover: 'rgba(255, 255, 255, 0.06)',
  glassBorder: 'rgba(255, 255, 255, 0.14)',
  glassBorderSubtle: 'rgba(255, 255, 255, 0.08)',
  uiSurfacePanel: '#090A0D',
  uiSurfaceCard: '#0F1117',
  uiSurfaceRail: '#171B24',
  uiSurfacePopover: '#15171C',
  uiSurfaceDrawer: '#08090C',
  uiSurfaceDrawerHeader: '#0D0F14',
  uiSurfaceDrawerMuted: 'rgba(255, 255, 255, 0.06)',
  uiSurfaceInput: 'rgba(255, 255, 255, 0.06)',
  uiSurfaceRowHover: 'rgba(255, 255, 255, 0.07)',
  uiSurfaceSelected: 'rgba(255, 255, 255, 0.08)',
  uiSurfaceWarning: 'rgba(255, 149, 0, 0.16)',
  uiBorderDefault: 'rgba(255, 255, 255, 0.14)',
  uiBorderSubtle: 'rgba(255, 255, 255, 0.08)',
  uiBorderStrong: 'rgba(255, 255, 255, 0.24)',
  uiBorderAccent: 'rgba(237, 237, 237, 0.24)',
  uiTextPrimary: '#EFF2F6',
  uiTextSecondary: '#C0C8D2',
  uiTextTertiary: '#9DA9B8',
  uiTextAccent: '#EDEDED',
  accentSolid: '#2A2A2A',
  accentOnSolid: '#FFFFFF',
  accentFg: '#EDEDED',
  accentMuted: 'rgba(237, 237, 237, 0.2)',
  accentSubtle: 'rgba(237, 237, 237, 0.08)',
  accentEmphasized: '#404650',
  accentChip: 'rgba(237, 237, 237, 0.08)',
  accentTraceSurface: 'rgba(237, 237, 237, 0.05)',
  accentTraceSurfaceHover: 'rgba(237, 237, 237, 0.08)',
  accentTraceBorder: 'rgba(237, 237, 237, 0.14)',
  accentTraceBorderHover: 'rgba(237, 237, 237, 0.18)',
  accentAnswerHoverBorder: 'rgba(237, 237, 237, 0.22)',
  accentSecondarySubtle: 'rgba(160, 160, 160, 0.12)',
  accentCommandBorder: 'rgba(237, 237, 237, 0.3)',
  accentStart: '#2A2A2A',
  accentEnd: '#4A4A4A',
  accentShadow: 'rgba(0, 0, 0, 0.24)',
  accentShadowHover: 'rgba(237, 237, 237, 0.16)',
  selectionBg: 'rgba(237, 237, 237, 0.12)',
  scrollbarThumb: 'rgba(255, 255, 255, 0.18)',
  scrollbarThumbHover: 'rgba(255, 255, 255, 0.28)',
  shadowElevated: '0 2px 12px rgba(0, 0, 0, 0.35), 0 1px 4px rgba(0, 0, 0, 0.28)',
  shadowFloating: '0 10px 32px rgba(0, 0, 0, 0.42), 0 4px 16px rgba(0, 0, 0, 0.32)',
  shadowPressed: '0 1px 3px rgba(0, 0, 0, 0.38), inset 0 1px 1px rgba(255, 255, 255, 0.04)',
  shadowInner: 'inset 0 1px 1px rgba(255, 255, 255, 0.08), inset 0 -1px 1px rgba(0, 0, 0, 0.18)',
  shadowGlow: '0 0 14px rgba(237, 237, 237, 0.08), 0 6px 18px rgba(0, 0, 0, 0.28)',
  shadowSubtle: '0 1px 6px rgba(0, 0, 0, 0.26)',
  shadowRing: 'none',
  luminousBlue: '#2A2A2A',
  luminousBlueGlow: 'rgba(237, 237, 237, 0.16)',
  luminousPurple: '#BF5AF2',
  luminousPurpleGlow: 'rgba(191, 90, 242, 0.35)',
  luminousPink: '#FF375F',
  luminousPinkGlow: 'rgba(255, 55, 95, 0.35)',
  luminousOrange: '#FF9F0A',
  luminousOrangeGlow: 'rgba(255, 159, 10, 0.35)',
  luminousGreen: '#30D158',
  luminousGreenGlow: 'rgba(48, 209, 88, 0.35)',
  luminousTeal: '#64D2FF',
  luminousTealGlow: 'rgba(100, 210, 255, 0.35)',
  luminousCyan: '#64D2FF',
  luminousCyanGlow: 'rgba(100, 210, 255, 0.35)',
  luminousIndigo: '#5E5CE6',
  luminousIndigoGlow: 'rgba(94, 92, 230, 0.35)',
  statusSuccessFg: '#30D158',
  statusSuccessBg: 'rgba(48, 209, 88, 0.18)',
  statusSuccessBorder: 'rgba(48, 209, 88, 0.32)',
  statusWarningFg: '#FFB340',
  statusWarningBg: 'rgba(255, 159, 10, 0.18)',
  statusWarningBorder: 'rgba(255, 159, 10, 0.34)',
  statusDangerFg: '#FF6B7A',
  statusDangerBg: 'rgba(255, 55, 95, 0.17)',
  statusDangerBorder: 'rgba(255, 55, 95, 0.34)',
  statusInfoFg: '#EDEDED',
  statusInfoBg: 'rgba(237, 237, 237, 0.08)',
  statusInfoBorder: 'rgba(237, 237, 237, 0.16)',
  statusNeutralFg: 'rgba(255, 255, 255, 0.74)',
  statusNeutralBg: 'rgba(255, 255, 255, 0.08)',
  statusNeutralBorder: 'rgba(255, 255, 255, 0.12)',
  rankGoldFg: '#F8D568',
  rankGoldBg: 'rgba(251, 191, 36, 0.18)',
  rankGoldBorder: 'rgba(251, 191, 36, 0.38)',
  rankSilverFg: '#CBD5E1',
  rankSilverBg: 'rgba(148, 163, 184, 0.16)',
  rankSilverBorder: 'rgba(148, 163, 184, 0.32)',
  rankBronzeFg: '#E9A76B',
  rankBronzeBg: 'rgba(180, 120, 60, 0.18)',
  rankBronzeBorder: 'rgba(180, 120, 60, 0.34)',
  dataVisibility: '#EDEDED',
  dataRank3: '#F8D568',
  dataRank1: '#30D158',
  dataMissed: '#FF6B7A',
  dataPositive: '#30D158',
  dataNegative: '#FF453A',
  dataNeutral: '#A1A1AA',
  dataOrange: '#FF9F0A',
  dataPurple: '#BF5AF2',
  dataCyan: '#64D2FF',
  dataPink: '#FF6B9A',
  dataTooltipBg: 'rgba(22, 22, 26, 0.96)',
  dataGrid: 'rgba(255, 255, 255, 0.07)',
  dataAxis: '#A1A1AA',
  dataCursor: 'rgba(255, 255, 255, 0.14)',
  chartSeries1: '#EDEDED',
  chartSeries2: '#BF5AF2',
  chartSeries3: '#FF9F0A',
  chartSeries4: '#30D158',
  chartSeries5: '#FF375F',
  chartSeries6: '#64D2FF',
  chartSeries7: '#5E5CE6',
  chartSeries8: '#FF7900',
  chartSeries9: '#06B6D4',
  chartSeries10: '#A78BFA',
  chartSeries11: '#F8D568',
  chartSeries12: '#34D399',
  chartSeries13: '#FF6B9A',
  chartSeries14: '#CBD5E1',
  chartSeries15: '#E9A76B',
};

const blackWhiteLight: DesignThemeCssTokens = {
  ...liquidGlassLight,
  bg: '#EBEBEB',
  bgWash: '#EBEBEB',
  bgDepth: '#EBEBEB',
  glassBg: '#FFFFFF',
  glassBgSubtle: '#FAFAFA',
  glassBgMuted: '#F5F5F5',
  glassBgBase: '#FFFFFF',
  glassBgElevated: '#FFFFFF',
  glassBgCard: '#FFFFFF',
  glassFill: '#F5F5F5',
  glassInteractiveHover: '#F0F0F0',
  glassTraceHeaderHover: '#F0F0F0',
  glassBorder: '#DEDEDE',
  glassBorderSubtle: '#EAEAEA',
  uiSurfacePanel: '#FFFFFF',
  uiSurfaceCard: '#FFFFFF',
  uiSurfaceRail: '#F7F7F7',
  uiSurfacePopover: '#FFFFFF',
  uiSurfaceDrawer: '#FAFAFA',
  uiSurfaceDrawerHeader: '#F5F5F5',
  uiSurfaceDrawerMuted: '#F5F5F5',
  uiSurfaceInput: '#F5F5F5',
  uiSurfaceRowHover: '#EEEEEE',
  uiSurfaceSelected: '#E8E8E8',
  uiBorderDefault: '#D6D6D6',
  uiBorderSubtle: '#E7E7E7',
  uiBorderStrong: '#B0B0B0',
  uiTextPrimary: '#171717',
  uiTextSecondary: '#525252',
  uiTextTertiary: '#666666',
  uiTextAccent: '#171717',
  accentSolid: '#171717',
  accentOnSolid: '#FFFFFF',
  accentFg: '#171717',
  accentEmphasized: '#333333',
  accentFocusRing: '#525252',
  accentStart: '#171717',
  accentEnd: '#171717',
  shadowFloating: '0 8px 24px rgba(0, 0, 0, 0.08)',
  shadowGlow: 'none',
  shadowInner: 'none',
  dataTooltipBg: '#FFFFFF',
  dataAxis: '#737373',
};

const blackWhiteDark: DesignThemeCssTokens = {
  ...liquidGlassDark,
  bg: '#111111',
  bgWash: '#111111',
  bgDepth: '#111111',
  glassBg: '#1D1D1D',
  glassBgSubtle: '#1D1D1D',
  glassBgMuted: '#202020',
  glassBgBase: '#1D1D1D',
  glassBgElevated: '#262626',
  glassBgCard: '#262626',
  glassFill: '#202020',
  glassInteractiveHover: '#292929',
  glassTraceHeaderHover: '#292929',
  glassBorder: '#353535',
  glassBorderSubtle: '#292929',
  uiSurfacePanel: '#1D1D1D',
  uiSurfaceCard: '#262626',
  uiSurfaceRail: '#1A1A1A',
  uiSurfacePopover: '#262626',
  uiSurfaceDrawer: '#1D1D1D',
  uiSurfaceDrawerHeader: '#1D1D1D',
  uiSurfaceDrawerMuted: '#202020',
  uiSurfaceInput: '#202020',
  uiSurfaceRowHover: '#292929',
  uiSurfaceSelected: '#353535',
  uiBorderDefault: '#3A3A3A',
  uiBorderSubtle: '#292929',
  uiBorderStrong: '#606060',
  uiTextPrimary: '#FAFAFA',
  uiTextSecondary: '#C2C2C2',
  uiTextTertiary: '#A3A3A3',
  uiTextAccent: '#FAFAFA',
  accentSolid: '#FAFAFA',
  accentOnSolid: '#171717',
  accentFg: '#FAFAFA',
  accentEmphasized: '#DEDEDE',
  accentFocusRing: '#D4D4D4',
  accentStart: '#FAFAFA',
  accentEnd: '#FAFAFA',
  shadowFloating: '0 8px 24px rgba(0, 0, 0, 0.3)',
  shadowGlow: 'none',
  shadowInner: 'none',
  dataTooltipBg: '#202020',
  dataAxis: '#A3A3A3',
};

const RAW_DESIGN_THEME_CSS_TOKENS: DesignThemeCssTokenMap = {
  'liquid-glass': {
    light: blackWhiteLight,
    dark: blackWhiteDark,
  },
  ...getGetdesignDeveloperToolThemeCss(liquidGlassLight, liquidGlassDark),
};

export const DESIGN_THEME_CSS_TOKENS = normalizeDesignThemeCssTokenMap(RAW_DESIGN_THEME_CSS_TOKENS);

export function getDesignThemeSwatches(themeId: DesignThemeId, mode: ResolvedDesignThemeMode) {
  const tokens = DESIGN_THEME_CSS_TOKENS[themeId][mode];
  return [tokens.accentSolid, tokens.bg, tokens.uiSurfaceRail, tokens.uiTextPrimary];
}

export function isDesignThemeId(value: unknown): value is DesignThemeId {
  return typeof value === 'string' && DESIGN_THEME_IDS.includes(value as DesignThemeId);
}

export function isDesignThemeMode(value: unknown): value is DesignThemeMode {
  return typeof value === 'string' && DESIGN_THEME_MODES.includes(value as DesignThemeMode);
}

export function parseDesignThemePreference(value: unknown): DesignThemePreference {
  if (!value || typeof value !== 'object') {
    return DEFAULT_DESIGN_THEME_PREFERENCE;
  }

  const candidate = value as Partial<Record<keyof DesignThemePreference, unknown>>;

  return {
    themeId: isDesignThemeId(candidate.themeId) ? candidate.themeId : DEFAULT_DESIGN_THEME_ID,
    mode: isDesignThemeMode(candidate.mode) ? candidate.mode : DEFAULT_DESIGN_THEME_MODE,
  };
}

function getDesignThemeCssTokens(
  themeId: DesignThemeId,
  mode: ResolvedDesignThemeMode
): DesignThemeCssTokens {
  return DESIGN_THEME_CSS_TOKENS[themeId][mode];
}

export function getDesignThemeTokenCache(
  themeId: DesignThemeId
): Record<ResolvedDesignThemeMode, DesignThemeCssTokens> {
  return DESIGN_THEME_CSS_TOKENS[themeId];
}

function applyCssTokenVariables(root: HTMLElement, tokens: DesignThemeCssTokens) {
  for (const [tokenKey, variableName] of CSS_TOKEN_VARIABLES) {
    const value = tokens[tokenKey];
    if (value == null || String(value).trim() === '') {
      continue;
    }
    root.style.setProperty(variableName, String(value));
  }

  root.style.setProperty(
    '--attune-theme-accent-gradient',
    `linear-gradient(135deg, ${tokens.accentStart} 0%, ${tokens.accentEnd} 100%)`
  );
}

export function applyDesignThemeCssVariables(
  root: HTMLElement,
  themeId: DesignThemeId,
  mode: ResolvedDesignThemeMode
) {
  applyCssTokenVariables(root, getDesignThemeCssTokens(themeId, mode));
}

export function getDesignThemeInitScript(): string {
  const knownThemeIdsJson = JSON.stringify(DESIGN_THEME_IDS);
  const designThemeTokensJson = JSON.stringify(DESIGN_THEME_CSS_TOKENS);
  const cssTokenVariablesJson = JSON.stringify(CSS_TOKEN_VARIABLES);

  return `
    (function () {
      function applyTokens(root, tokens, tokenVariables) {
        for (var i = 0; i < tokenVariables.length; i += 1) {
          var tokenKey = tokenVariables[i][0];
          var variableName = tokenVariables[i][1];
          var v = tokens[tokenKey];
          if (v == null || String(v).trim() === '') {
            continue;
          }
          root.style.setProperty(variableName, String(v));
        }
        root.style.setProperty(
          '--attune-theme-accent-gradient',
          'linear-gradient(135deg, ' + tokens.accentStart + ' 0%, ' + tokens.accentEnd + ' 100%)'
        );
      }

      var storageKey = ${JSON.stringify(DESIGN_THEME_STORAGE_KEY)};
      var knownThemeIds = ${knownThemeIdsJson};
      var defaultTheme = ${JSON.stringify(DEFAULT_DESIGN_THEME_ID)};
      var defaultMode = ${JSON.stringify(DEFAULT_DESIGN_THEME_MODE)};
      var designThemeTokens = ${designThemeTokensJson};
      var tokenVariables = ${cssTokenVariablesJson};

      try {
        var stored = window.localStorage.getItem(storageKey);
        var preference = stored ? JSON.parse(stored) : {};
        var themeId = knownThemeIds.indexOf(preference.themeId) >= 0 ? preference.themeId : defaultTheme;
        var mode = preference.mode === 'light' || preference.mode === 'dark' || preference.mode === 'system'
          ? preference.mode
          : defaultMode;
        var resolvedMode = mode === 'system'
          ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
          : mode;
        var themeTokenMap = designThemeTokens[themeId] || designThemeTokens[defaultTheme];
        var themeTokens = themeTokenMap[resolvedMode] || themeTokenMap.light;
        var root = document.documentElement;

        root.setAttribute('data-attune-theme', themeId);
        root.setAttribute('data-attune-mode', resolvedMode);
        root.setAttribute('data-attune-theme-mode', mode);
        root.setAttribute('data-color-mode', resolvedMode);
        root.setAttribute('data-theme', resolvedMode);
        root.style.colorScheme = resolvedMode;
        root.classList.toggle('dark', resolvedMode === 'dark');
        root.classList.toggle('light', resolvedMode === 'light');
        applyTokens(root, themeTokens, tokenVariables);
      } catch (error) {
        var fallbackRoot = document.documentElement;
        var fbResolved = defaultMode === 'system'
          ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
          : defaultMode;
        var defaultThemeTokens = designThemeTokens[defaultTheme];
        var fbTokens = defaultThemeTokens[fbResolved] || defaultThemeTokens.light;
        fallbackRoot.setAttribute('data-attune-theme', defaultTheme);
        fallbackRoot.setAttribute('data-attune-mode', fbResolved);
        fallbackRoot.setAttribute('data-attune-theme-mode', defaultMode);
        fallbackRoot.setAttribute('data-color-mode', fbResolved);
        fallbackRoot.setAttribute('data-theme', fbResolved);
        fallbackRoot.style.colorScheme = fbResolved;
        fallbackRoot.classList.toggle('dark', fbResolved === 'dark');
        fallbackRoot.classList.toggle('light', fbResolved === 'light');
        applyTokens(fallbackRoot, fbTokens, tokenVariables);
      }
    })();
  `;
}
